import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import { Bell } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import '../styles/Notifications.css';

function Notifications() {
  const { authUserId, selectedWorkspaceId } = useWorkspace();
  const { language } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const fetchNotifications = async () => {
    if (!authUserId || !selectedWorkspaceId) return;
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .match({ is_read: false, user_id: authUserId, workspace_id: selectedWorkspaceId });
    if (error) {
      console.error('Error fetching notifications:', error.message);
    } else {
      setNotifications(data);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [authUserId, selectedWorkspaceId]);

  useEffect(() => {
    if (!selectedWorkspaceId) return;

    const channel = supabase.channel('realtime-tasks');

    channel.on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'tasks' },
      async payload => {
        console.log('New task payload received:', payload);
        const newTask = payload.new;
        let assigneesArray = newTask.assignees;
        if (typeof assigneesArray === 'string') {
          assigneesArray = assigneesArray.replace(/[{}]/g, '').split(',');
        }
        if (Array.isArray(assigneesArray)) {
          for (const assignee of assigneesArray) {
            const trimmedAssignee = assignee.trim();
            const newNotification = {
              id: uuidv4(),
              created_at: new Date().toISOString(), 
              workspace_id: newTask.workspace_id, 
              created_by: "849c8b41-d265-49de-8d86-e771a5c0fc50",
              user_id: trimmedAssignee,
              notification: language === 'en' ? "you have one new request" : "Sie haben eine neue Anfrage",
              is_read: false,
            };

            console.log("Inserting notification with parameters:", {
              id: newNotification.id,
              created_at: newNotification.created_at,
              workspace_id: newNotification.workspace_id,
              created_by: newNotification.created_by,
              user_id: newNotification.user_id,
              notification: newNotification.notification,
              is_read: newNotification.is_read,
            });

            const { error } = await supabase
              .from('notifications')
              .insert([newNotification]);
            if (error) {
              console.error('Error inserting notification:', error.message);
            } else {
              if (trimmedAssignee === authUserId) {
                setNotifications(prev => [...prev, newNotification]);
              }
            }
          }
        }
      }
    );

    const subscription = channel.subscribe(status => {
      console.log('Subscription status:', status);
    });

    return () => {
      channel.unsubscribe();
    };
  }, [selectedWorkspaceId, authUserId, language]);

  useEffect(() => {
    const handleCloseNotifications = () => {
      setShowDropdown(false);
    };

    window.addEventListener('closeNotifications', handleCloseNotifications);

    return () => {
      window.removeEventListener('closeNotifications', handleCloseNotifications);
    };
  }, []);

  const markAsRead = async (notificationId) => {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
    if (error) {
      console.error('Error marking notification as read:', error.message);
    } else {
      setNotifications(notifications.filter(notif => notif.id !== notificationId));
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="notifications-container">
      <button 
        className="notifications-button" 
        onClick={toggleDropdown}
        title={language === 'en' ? 'Notifications' : 'Benachrichtigungen'}
      >
        <Bell className="bell-icon" fill="none" stroke="currentColor" />
        <span className="notification-count">{notifications.length}</span>
      </button>
      {showDropdown && (
        <div className="notifications-dropdown">
          {notifications.length === 0 ? (
            <div className="notification-item">
              {language === 'en' ? 'No notifications' : 'Keine Benachrichtigungen'}
            </div>
          ) : (
            notifications.map(notif => (
              <div 
                key={notif.id} 
                className="notification-item"
                onClick={() => markAsRead(notif.id)}
              >
                <ReactMarkdown>{notif.notification}</ReactMarkdown>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default Notifications;
